import React from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Share2, Podcast, Video, BookOpen, Compass, X } from "lucide-react";

const GOALS = [
  { icon: Radio, title: "Start My Radio Station", tint: "text-brand-amber bg-brand-amber/10 border-brand-amber/30",
    desc: "Connect your radio station, streaming provider, metadata, branding, and station automation.",
    cta: "Set Up Radio Station", to: "/onboarding", testid: "goal-station" },
  { icon: Share2, title: "Automate My Social Media", tint: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
    desc: "Connect your social accounts and begin creating and scheduling AI-powered posts.",
    cta: "Set Up Social Media", to: "/dashboard/social-connections", testid: "goal-social" },
  { icon: Podcast, title: "Create a Podcast", tint: "text-violet-300 bg-violet-500/10 border-violet-500/30",
    desc: "Upload audio, create podcast episodes, generate show notes, descriptions, titles, and promo content.",
    cta: "Create My First Podcast", to: "/dashboard/podcast-creator", testid: "goal-podcast" },
  { icon: Video, title: "Create a Music Video", tint: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    desc: "Upload a finished song and use Music Video Studio to create an AI-assisted music video.",
    cta: "Open Music Video Studio", to: "/dashboard/vision-studio", testid: "goal-video" },
  { icon: BookOpen, title: "Create a Digital Magazine", tint: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    desc: "Create your publication, branding, articles, artist features, interviews, and digital issues.",
    cta: "Start My Magazine", to: "/dashboard/magazine", testid: "goal-magazine" },
  { icon: Compass, title: "Show Me Everything", tint: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    desc: "Take a complete guided tour of BroadcastFlow AI.",
    cta: "Start Full Tour", to: "__tour__", testid: "goal-tour" },
];

export default function GoalCards({ onClose, onFullTour }) {
  const navigate = useNavigate();

  const pick = (g) => {
    if (g.to === "__tour__") { onFullTour(); return; }
    onClose();
    navigate(g.to);
  };

  return (
    <div className="fixed inset-0 z-[95] overflow-y-auto" data-testid="tour-goal-cards">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative min-h-full flex items-center justify-center p-4 sm:p-8">
        <div className="relative w-full max-w-4xl rounded-2xl border border-line bg-bg-surface p-6 sm:p-10 shadow-2xl animate-fade-up">
          <button onClick={onClose} data-testid="tour-goals-close"
            className="absolute right-4 top-4 text-ink-muted hover:text-ink-primary transition"><X className="h-4 w-4" /></button>
          <div className="mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-amber mb-2">// Getting started</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink-primary">What would you like to do first?</h2>
            <p className="text-sm text-ink-secondary mt-1">Pick a goal — we'll take you straight there.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GOALS.map((g, i) => (
              <button key={g.testid} onClick={() => pick(g)} data-testid={g.testid}
                className="group text-left rounded-xl border border-line bg-bg-elevated/50 p-5 hover:border-brand-amber/60 hover:bg-bg-elevated transition"
                style={{ animation: "fade-up .4s ease both", animationDelay: `${i * 50}ms` }}>
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border mb-3 ${g.tint}`}>
                  <g.icon className="h-5 w-5" />
                </div>
                <div className="text-sm font-bold text-ink-primary">{g.title}</div>
                <div className="text-[11px] text-ink-muted mt-1 leading-relaxed">{g.desc}</div>
                <div className="mt-3 text-xs font-bold text-brand-amber group-hover:underline">{g.cta} →</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
